import { Injectable } from '@angular/core';

import { ComponentStore } from '@ngrx/component-store';
import {
  collection,
  query,
  orderBy,
  where,
  onSnapshot,
  type Unsubscribe,
} from 'firebase/firestore';

import { db } from './firebase';
import type {
  GlobalIndex,
  BranchIndex,
  BranchSummary,
  AppDeployment,
  AppStatus,
} from '../types/dynamics.types';

const STATUS_PRIORITY: AppStatus[] = [
  'failed',
  'queued',
  'building',
  'built',
  'deploying',
  'deployed',
];

function worstStatus(apps: AppDeployment[]): AppStatus {
  for (const status of STATUS_PRIORITY) {
    if (apps.some(a => a.status === status)) return status;
  }
  return 'queued';
}

interface FirestoreDeployment {
  dynamicId: string;
  appName: string;
  status: AppStatus;
  branchName: string;
  commitSha: string;
  actor: string;
  pipelineUrl: string;
  buildConfig: string;
  queuedAt: string | null;
  buildStartedAt: string | null;
  buildFinishedAt: string | null;
  buildDurationMs: number | null;
  bundleSizeBytes: number | null;
  deployedAt: { seconds: number } | string | null;
  url: string | null;
  error: string | null;
  updatedAt: { seconds: number } | null;
}

function toIsoString(val: { seconds: number } | string | null): string | null {
  if (!val) return null;
  if (typeof val === 'string') return val;
  return new Date(val.seconds * 1000).toISOString();
}

function toAppDeployment(doc: FirestoreDeployment): AppDeployment {
  return {
    appName: doc.appName,
    status: doc.status ?? 'queued',
    queuedAt: doc.queuedAt ?? '',
    branchName: doc.branchName ?? '',
    commitSha: doc.commitSha ?? '',
    actor: doc.actor ?? '',
    pipelineUrl: doc.pipelineUrl ?? '',
    buildConfig: doc.buildConfig ?? '',
    buildStartedAt: doc.buildStartedAt ?? null,
    buildFinishedAt: doc.buildFinishedAt ?? null,
    buildDurationMs: doc.buildDurationMs ?? null,
    bundleSizeBytes: doc.bundleSizeBytes ?? null,
    deployedAt: toIsoString(doc.deployedAt),
    url: doc.url ?? null,
    error: doc.error ?? null,
  };
}

function buildGlobalIndex(docs: FirestoreDeployment[]): GlobalIndex {
  const byBranch = new Map<string, FirestoreDeployment[]>();
  for (const data of docs) {
    if (!data.dynamicId) continue;
    const existing = byBranch.get(data.dynamicId) ?? [];
    existing.push(data);
    byBranch.set(data.dynamicId, existing);
  }

  const branches: BranchSummary[] = [];
  for (const [dynamicId, branchDocs] of byBranch) {
    const apps = branchDocs.map(toAppDeployment);
    const latest = branchDocs.reduce((a, b) => {
      const aTime = a.updatedAt?.seconds ?? 0;
      const bTime = b.updatedAt?.seconds ?? 0;
      return aTime > bTime ? a : b;
    });

    branches.push({
      dynamicId,
      branchName: latest.branchName ?? '',
      lastUpdatedAt: toIsoString(latest.updatedAt) ?? '',
      appCount: apps.length,
      actor: latest.actor ?? '',
      pipelineUrl: latest.pipelineUrl ?? '',
      status: worstStatus(apps),
    });
  }

  branches.sort(
    (a, b) => new Date(b.lastUpdatedAt).getTime() - new Date(a.lastUpdatedAt).getTime(),
  );

  return { generatedAt: new Date().toISOString(), branches };
}

function buildBranchIndex(dynamicId: string, docs: FirestoreDeployment[]): BranchIndex {
  const apps = docs.map(toAppDeployment);
  const latest = docs.reduce((a, b) => {
    const aTime = a.updatedAt?.seconds ?? 0;
    const bTime = b.updatedAt?.seconds ?? 0;
    return aTime > bTime ? a : b;
  }, docs[0]);

  return {
    dynamicId,
    branchName: latest?.branchName ?? '',
    generatedAt: new Date().toISOString(),
    apps,
  };
}

interface DynamicsState {
  globalIndex: GlobalIndex | null;
  globalLoading: boolean;
  globalError: string | null;

  branchIndex: BranchIndex | null;
  branchLoading: boolean;
  branchError: string | null;
}

const INITIAL_STATE: DynamicsState = {
  globalIndex: null,
  globalLoading: false,
  globalError: null,

  branchIndex: null,
  branchLoading: false,
  branchError: null,
};

@Injectable({ providedIn: 'root' })
export class DynamicsDataService extends ComponentStore<DynamicsState> {
  private readonly deploymentsRef = collection(db, 'deployments');

  public readonly globalIndex = this.selectSignal(s => s.globalIndex);
  public readonly loading = this.selectSignal(s => s.globalLoading);
  public readonly error = this.selectSignal(s => s.globalError);

  public readonly branchIndex = this.selectSignal(s => s.branchIndex);
  public readonly branchLoading = this.selectSignal(s => s.branchLoading);
  public readonly branchError = this.selectSignal(s => s.branchError);

  private globalUnsub: Unsubscribe | null = null;
  private branchUnsub: Unsubscribe | null = null;

  constructor() {
    super(INITIAL_STATE);
  }

  public startPolling(): void {
    this.stopPolling();
    this.patchState({ globalLoading: true });

    const q = query(this.deploymentsRef, orderBy('updatedAt', 'desc'));

    this.globalUnsub = onSnapshot(
      q,
      snapshot => {
        const docs = snapshot.docs.map(doc => <FirestoreDeployment>doc.data());
        this.patchState({
          globalLoading: false,
          globalError: null,
          globalIndex: buildGlobalIndex(docs),
        });
      },
      err => {
        this.patchState({
          globalLoading: false,
          globalError: err.message ?? 'Failed to fetch deployments',
        });
      },
    );
  }

  public stopPolling(): void {
    this.globalUnsub?.();
    this.globalUnsub = null;
  }

  public subscribeToBranch(dynamicId: string): void {
    this.unsubscribeFromBranch();
    this.patchState({ branchLoading: true });

    const q = query(this.deploymentsRef, where('dynamicId', '==', dynamicId));

    this.branchUnsub = onSnapshot(
      q,
      snapshot => {
        const docs = snapshot.docs.map(doc => <FirestoreDeployment>doc.data());
        this.patchState({
          branchLoading: false,
          branchError: null,
          branchIndex: buildBranchIndex(dynamicId, docs),
        });
      },
      err => {
        this.patchState({
          branchLoading: false,
          branchError: err.message ?? 'Failed to fetch branch',
        });
      },
    );
  }

  public unsubscribeFromBranch(): void {
    this.branchUnsub?.();
    this.branchUnsub = null;
  }

  public override ngOnDestroy(): void {
    this.stopPolling();
    this.unsubscribeFromBranch();
    super.ngOnDestroy();
  }
}
